import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { ThemeProvider, CssBaseline } from '@mui/material'
import { createTheme } from '@mui/material/styles'
import {
  MAROON,
  GOLD,
  DARK_BG,
  DARK_PAPER,
  DARK_ACCENT,
  AUTH_DARK_GRAD_START,
  AUTH_DARK_GRAD_END,
  AUTH_LIGHT_GRAD_START,
  AUTH_LIGHT_GRAD_END,
} from '../theme/tokens'

// Augment MUI palette with custom entries so components can use theme.palette.*
// instead of importing raw hex values directly.
declare module '@mui/material/styles' {
  interface Palette {
    authGradient: { start: string; end: string }
  }
  interface PaletteOptions {
    authGradient?: { start: string; end: string }
  }
}

export type ColorMode = 'auto' | 'light' | 'dark'
export type ResolvedMode = 'light' | 'dark'

type ThemeModeContextType = {
  colorMode: ColorMode
  mode: ResolvedMode
  cycleMode: () => void
}

const ThemeModeContext = createContext<ThemeModeContextType | null>(null)

const CYCLE: ColorMode[] = ['auto', 'light', 'dark']

function getSystemMode(): ResolvedMode {
  return globalThis.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function resolve(colorMode: ColorMode): ResolvedMode {
  return colorMode === 'auto' ? getSystemMode() : colorMode
}

function getInitialColorMode(): ColorMode {
  const stored = localStorage.getItem('mac_theme') as ColorMode | null
  if (stored === 'light' || stored === 'dark' || stored === 'auto') return stored
  return 'auto'
}

function buildTheme(mode: ResolvedMode) {
  return createTheme({
    palette: {
      mode,
      primary: { main: MAROON },
      secondary: { main: GOLD },
      authGradient: {
        start: mode === 'dark' ? AUTH_DARK_GRAD_START : AUTH_LIGHT_GRAD_START,
        end: mode === 'dark' ? AUTH_DARK_GRAD_END : AUTH_LIGHT_GRAD_END,
      },
      ...(mode === 'dark' && {
        background: { default: DARK_BG, paper: DARK_PAPER },
      }),
    },
    ...(mode === 'dark' && {
      components: {
        MuiInputLabel: {
          styleOverrides: {
            root: { '&.Mui-focused': { color: DARK_ACCENT } },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            // rgba(255,255,255,0.5) on DARK_PAPER → ~5.6:1 (WCAG non-text contrast)
            notchedOutline: { borderColor: 'rgba(255,255,255,0.5)' },
            root: {
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: DARK_ACCENT },
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            // Use gold for contained primary buttons in dark mode:
            // GOLD on DARK_PAPER → ~11.9:1 (WCAG AAA), MAROON text on GOLD → ~10.8:1
            containedPrimary: {
              backgroundColor: GOLD,
              color: MAROON,
              '&:hover': { backgroundColor: '#E5AC4F' },
              '&.Mui-disabled': {
                backgroundColor: 'rgba(253, 191, 87, 0.2)',
                color: 'rgba(122, 0, 60, 0.4)',
              },
            },
            outlinedPrimary: {
              borderColor: DARK_ACCENT,
              color: DARK_ACCENT,
              '&:hover': {
                borderColor: DARK_ACCENT,
                backgroundColor: 'rgba(208, 112, 152, 0.08)',
              },
            },
          },
        },
        MuiLink: {
          styleOverrides: {
            // DARK_ACCENT on DARK_PAPER → ~6.2:1 (WCAG AA)
            root: {
              color: DARK_ACCENT,
              '&:hover': { color: '#E090B8' },
            },
          },
        },
      },
    }),
  })
}

export function ThemeModeProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [colorMode, setColorMode] = useState<ColorMode>(getInitialColorMode)

  const cycleMode = useCallback(() => {
    setColorMode((prev) => {
      const next = CYCLE[(CYCLE.indexOf(prev) + 1) % CYCLE.length]
      localStorage.setItem('mac_theme', next)
      return next
    })
  }, [])

  const mode = resolve(colorMode)
  const theme = useMemo(() => buildTheme(mode), [mode])
  const ctx = useMemo(() => ({ colorMode, mode, cycleMode }), [colorMode, mode, cycleMode])

  return (
    <ThemeModeContext.Provider value={ctx}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  )
}

export function useThemeMode(): ThemeModeContextType {
  const ctx = useContext(ThemeModeContext)
  if (!ctx) throw new Error('useThemeMode must be used within ThemeModeProvider')
  return ctx
}
